/**
 * C4 scaffold — Custom Rules Ask | Allow | Never tool-filter draft
 *
 * Persists under pulse_preferences.card_type_preferences.meta.crew_custom_rules_draft
 * (same jsonb nest as interest_brief / briefing_schedule / crew_member_draft —
 * no migration).
 *
 * Honesty boundaries (do not weaken):
 * - Never strips for send/buy/delete/computer via filterToolsForCustomRules
 * - enforcementStatus stamps `WIRED` when isCrewCustomRulesEnforcementWired()
 * - Do NOT claim full GATE B (unassigned Member never mutates) from this alone
 * - UI may save Ask|Allow|Never for enforced keys
 *
 * No Muse/Vesper. Preserve LosiAnimated. Metro cannot import this file —
 * mobile mirrors in mobile/lib/crew/custom-rules.ts.
 */

/** Prefs stamp while filter is not live. */
export const CREW_CUSTOM_RULES_ENFORCEMENT_NOT_WIRED = 'NOT_WIRED' as const

/** Prefs stamp when Never strips are live in tools-stream. */
export const CREW_CUSTOM_RULES_ENFORCEMENT_WIRED = 'WIRED' as const

export type CrewCustomRulesEnforcementStatus =
  | typeof CREW_CUSTOM_RULES_ENFORCEMENT_NOT_WIRED
  | typeof CREW_CUSTOM_RULES_ENFORCEMENT_WIRED

/** Prefs meta key — nest under card_type_preferences.meta. */
export const CREW_CUSTOM_RULES_DRAFT_META_KEY = 'crew_custom_rules_draft' as const

/** Top-level PUT alias — merged into meta.crew_custom_rules_draft on write. */
export const CREW_CUSTOM_RULES_DRAFT_ALIAS = 'crew_custom_rules_draft' as const

/** Ask | Allow | Never — draft intent only while enforcement is NOT_WIRED. */
export const CREW_CUSTOM_RULE_MODE_ASK = 'ask' as const
export const CREW_CUSTOM_RULE_MODE_ALLOW = 'allow' as const
export const CREW_CUSTOM_RULE_MODE_NEVER = 'never' as const

export const CREW_CUSTOM_RULE_MODES = [
  CREW_CUSTOM_RULE_MODE_ASK,
  CREW_CUSTOM_RULE_MODE_ALLOW,
  CREW_CUSTOM_RULE_MODE_NEVER
] as const

export type CrewCustomRuleMode = (typeof CREW_CUSTOM_RULE_MODES)[number]

/**
 * Named tool-family slots for the draft filter (not live tool ids).
 * Defaults match C3 copy: Ask send/buy/delete; computer stays Ask in the
 * draft until C4 server filter ships (C2 plan-gate is separate — live via
 * propose-plan / tools-stream, not Custom Rules prefs).
 */
export const CREW_CUSTOM_RULE_TOOL_KEYS = [
  'send',
  'buy',
  'delete',
  'computer'
] as const

export type CrewCustomRuleToolKey = (typeof CREW_CUSTOM_RULE_TOOL_KEYS)[number]

/** Section title — collapsed Custom Rules on Meet-your-Crew. */
export const CREW_CUSTOM_RULES_TITLE = 'Rules'

/** Short supporting line — silenced (anti-slop; HONESTY covers NOT_WIRED). */
export const CREW_CUSTOM_RULES_HINT = '' as const

/**
 * Honesty banner — short warm line (no GATE B / NOT_WIRED jargon).
 * Hosts must gate mounts when empty.
 */
export const CREW_CUSTOM_RULES_HONESTY =
  'Never blocks that tool. Ask checks with you first.' as const

/** Visible enforcement chip — silenced (anti-slop; no enum tokens). */
export const CREW_CUSTOM_RULES_ENFORCEMENT_LABEL = '' as const

/** Collapsed section show label. */
export const CREW_CUSTOM_RULES_SECTION_LABEL = 'Rules'

/** Collapsed section hide label. */
export const CREW_CUSTOM_RULES_SECTION_HIDE_LABEL = 'Hide'

/** Panel id for aria-controls. */
export const CREW_CUSTOM_RULES_PANEL_ID = 'crew-custom-rules-panel' as const

/** Per-tool mode group a11y prefix. */
export const CREW_CUSTOM_RULES_MODE_GROUP_A11Y_PREFIX = 'Rule' as const

/** Mode button labels (UI). */
export const CREW_CUSTOM_RULE_MODE_ASK_LABEL = 'Ask'
export const CREW_CUSTOM_RULE_MODE_ALLOW_LABEL = 'Allow'
export const CREW_CUSTOM_RULE_MODE_NEVER_LABEL = 'Never'

/** Tool-family row labels. */
export const CREW_CUSTOM_RULE_TOOL_LABELS: Record<
  CrewCustomRuleToolKey,
  string
> = {
  send: 'Send',
  buy: 'Buy',
  delete: 'Delete',
  computer: 'Computer'
}

/** Save draft CTA. */
export const CREW_CUSTOM_RULES_SAVE_LABEL = 'Save'

/** Save draft a11y — no NOT_WIRED enum token in product chrome. */
export const CREW_CUSTOM_RULES_SAVE_A11Y = 'Save'

/** Toast / Alert — load failure. */
export const CREW_CUSTOM_RULES_LOAD_ERROR =
  'Could not' as const

/** Toast / Alert — save failure. */
export const CREW_CUSTOM_RULES_SAVE_ERROR =
  'Could not' as const

/**
 * Toast / Alert — save success. Hosts must gate toast when empty.
 */
export const CREW_CUSTOM_RULES_SAVE_SUCCESS = 'Saved.' as const

/**
 * Closed chrome set (web ↔ mobile lockstep).
 * Prefer named exports at call sites; MIRROR is for parity audits.
 */
export const CREW_CUSTOM_RULES_MIRROR = {
  CREW_CUSTOM_RULES_TITLE,
  CREW_CUSTOM_RULES_HINT,
  CREW_CUSTOM_RULES_HONESTY,
  CREW_CUSTOM_RULES_ENFORCEMENT_LABEL,
  CREW_CUSTOM_RULES_ENFORCEMENT_NOT_WIRED,
  CREW_CUSTOM_RULES_SECTION_LABEL,
  CREW_CUSTOM_RULES_SECTION_HIDE_LABEL,
  CREW_CUSTOM_RULES_PANEL_ID,
  CREW_CUSTOM_RULES_MODE_GROUP_A11Y_PREFIX,
  CREW_CUSTOM_RULE_MODE_ASK,
  CREW_CUSTOM_RULE_MODE_ALLOW,
  CREW_CUSTOM_RULE_MODE_NEVER,
  CREW_CUSTOM_RULE_MODE_ASK_LABEL,
  CREW_CUSTOM_RULE_MODE_ALLOW_LABEL,
  CREW_CUSTOM_RULE_MODE_NEVER_LABEL,
  CREW_CUSTOM_RULES_SAVE_LABEL,
  CREW_CUSTOM_RULES_SAVE_A11Y,
  CREW_CUSTOM_RULES_LOAD_ERROR,
  CREW_CUSTOM_RULES_SAVE_ERROR,
  CREW_CUSTOM_RULES_SAVE_SUCCESS
} as const

/**
 * Draft shape stored in prefs. enforcementStatus mirrors live Never strips
 * (WIRED) or NOT_WIRED when the filter is off — never invent GATE B alone.
 */
export type CrewCustomRulesDraft = {
  send: CrewCustomRuleMode
  buy: CrewCustomRuleMode
  delete: CrewCustomRuleMode
  computer: CrewCustomRuleMode
  enforcementStatus: CrewCustomRulesEnforcementStatus
}

/** Honest prefs stamp for the current runtime. */
export function crewCustomRulesEnforcementStamp(): CrewCustomRulesEnforcementStatus {
  return isCrewCustomRulesEnforcementWired()
    ? CREW_CUSTOM_RULES_ENFORCEMENT_WIRED
    : CREW_CUSTOM_RULES_ENFORCEMENT_NOT_WIRED
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

/** Normalize one Ask|Allow|Never mode; invalid → ask. */
export function normalizeCrewCustomRuleMode(raw: unknown): CrewCustomRuleMode {
  if (typeof raw !== 'string') return CREW_CUSTOM_RULE_MODE_ASK
  const lower = raw.trim().toLowerCase()
  if (lower === CREW_CUSTOM_RULE_MODE_ALLOW) return CREW_CUSTOM_RULE_MODE_ALLOW
  if (lower === CREW_CUSTOM_RULE_MODE_NEVER) return CREW_CUSTOM_RULE_MODE_NEVER
  if (lower === CREW_CUSTOM_RULE_MODE_ASK) return CREW_CUSTOM_RULE_MODE_ASK
  // Accept product phrasing "Ask first"
  if (lower === 'ask_first' || lower === 'ask-first') {
    return CREW_CUSTOM_RULE_MODE_ASK
  }
  return CREW_CUSTOM_RULE_MODE_ASK
}

/**
 * Default draft — Ask on send/buy/delete/computer.
 * enforcementStatus mirrors live Never-strip wiring.
 */
export function defaultCrewCustomRulesDraft(): CrewCustomRulesDraft {
  return {
    send: CREW_CUSTOM_RULE_MODE_ASK,
    buy: CREW_CUSTOM_RULE_MODE_ASK,
    delete: CREW_CUSTOM_RULE_MODE_ASK,
    computer: CREW_CUSTOM_RULE_MODE_ASK,
    enforcementStatus: crewCustomRulesEnforcementStamp()
  }
}

/**
 * Normalize any raw draft-like input into an honest CrewCustomRulesDraft.
 * Stamps WIRED when Never strips are live.
 */
export function normalizeCrewCustomRulesDraft(
  raw: unknown
): CrewCustomRulesDraft | null {
  if (raw == null) return null
  if (!isRecord(raw)) return null

  const rulesSource = isRecord(raw.rules) ? raw.rules : raw

  return {
    send: normalizeCrewCustomRuleMode(rulesSource.send),
    buy: normalizeCrewCustomRuleMode(rulesSource.buy),
    delete: normalizeCrewCustomRuleMode(rulesSource.delete),
    computer: normalizeCrewCustomRuleMode(rulesSource.computer),
    enforcementStatus: crewCustomRulesEnforcementStamp()
  }
}

/**
 * Read crew_custom_rules_draft from card_type_preferences jsonb.
 */
export function extractCrewCustomRulesDraft(
  cardTypePreferences: unknown
): CrewCustomRulesDraft | null {
  if (!isRecord(cardTypePreferences)) return null
  const meta = cardTypePreferences.meta
  if (!isRecord(meta)) return null
  return normalizeCrewCustomRulesDraft(meta[CREW_CUSTOM_RULES_DRAFT_META_KEY])
}

/**
 * Merge a Custom Rules draft into card_type_preferences without dropping
 * toggles or sibling meta (interest_brief / briefing_schedule /
 * crew_member_draft). Pass null to clear. Stamps live enforcement honesty.
 */
export function mergeCrewCustomRulesDraft(
  existing: unknown,
  draft: CrewCustomRulesDraft | null
): Record<string, unknown> {
  const base: Record<string, unknown> = isRecord(existing) ? { ...existing } : {}
  const priorMeta = isRecord(base.meta) ? { ...base.meta } : {}

  if (draft == null) {
    delete priorMeta[CREW_CUSTOM_RULES_DRAFT_META_KEY]
    if (Object.keys(priorMeta).length === 0) {
      delete base.meta
    } else {
      base.meta = priorMeta
    }
    return base
  }

  const normalized =
    normalizeCrewCustomRulesDraft(draft) ?? defaultCrewCustomRulesDraft()
  const stored: CrewCustomRulesDraft = {
    send: normalized.send,
    buy: normalized.buy,
    delete: normalized.delete,
    computer: normalized.computer,
    enforcementStatus: crewCustomRulesEnforcementStamp()
  }

  base.meta = {
    ...priorMeta,
    [CREW_CUSTOM_RULES_DRAFT_META_KEY]: stored
  }
  return base
}

/** Zod-friendly plain object for API bodies. */
export function crewCustomRulesDraftToJson(
  draft: CrewCustomRulesDraft
): Record<string, string> {
  const normalized =
    normalizeCrewCustomRulesDraft(draft) ?? defaultCrewCustomRulesDraft()
  return {
    send: normalized.send,
    buy: normalized.buy,
    delete: normalized.delete,
    computer: normalized.computer,
    enforcementStatus: crewCustomRulesEnforcementStamp()
  }
}

/**
 * Server tool filter — Never strips send/buy/delete/computer families.
 * Ask / Allow leave tools present (Ask still hits confirmation / plan-gate).
 * Computer Never is belt-and-suspenders with member-access.
 */
export function filterToolsForCustomRules<
  T extends { function?: { name?: string }; name?: string }
>(tools: T[], draft?: CrewCustomRulesDraft | null): T[] {
  if (!draft) return tools

  const stripSend = draft.send === CREW_CUSTOM_RULE_MODE_NEVER
  const stripBuy = draft.buy === CREW_CUSTOM_RULE_MODE_NEVER
  const stripDelete = draft.delete === CREW_CUSTOM_RULE_MODE_NEVER
  const stripComputer = draft.computer === CREW_CUSTOM_RULE_MODE_NEVER
  if (!stripSend && !stripBuy && !stripDelete && !stripComputer) return tools

  return tools.filter(tool => {
    const name = (
      tool.function?.name ||
      tool.name ||
      ''
    ).toLowerCase()
    if (!name) return true
    if (stripSend && isCrewSendFamilyToolName(name)) return false
    if (stripBuy && isCrewBuyFamilyToolName(name)) return false
    if (stripDelete && isCrewDeleteFamilyToolName(name)) return false
    if (stripComputer && isCrewComputerFamilyToolName(name)) return false
    return true
  })
}

/** Match Nexus CRM / outbound send tool names. */
export function isCrewSendFamilyToolName(toolName: string): boolean {
  const n = toolName.toLowerCase()
  return (
    n.includes('send_sms') ||
    n.includes('send_email') ||
    n.includes('send_from_template') ||
    n === 'send_email_tool' ||
    n === 'send_sms_tool' ||
    (n.includes('nexus_crm') && n.includes('send')) ||
    n.includes('bulk_email') ||
    n.includes('bulk_sms')
  )
}

/** Match purchase / checkout / buy tool names. */
export function isCrewBuyFamilyToolName(toolName: string): boolean {
  const n = toolName.toLowerCase()
  return (
    n.includes('purchase') ||
    n.includes('checkout') ||
    n.includes('buy_') ||
    n.includes('_buy') ||
    n.includes('place_order') ||
    n.includes('stripe_pay')
  )
}

/** Match destructive delete tool names (not read/list). */
export function isCrewDeleteFamilyToolName(toolName: string): boolean {
  const n = toolName.toLowerCase()
  if (n.includes('list') || n.includes('get_') || n.includes('search')) {
    return false
  }
  return (
    n.includes('delete_') ||
    n.includes('_delete') ||
    n.includes('remove_') ||
    n.includes('destroy_') ||
    n.endsWith('__delete') ||
    n.includes('trash_')
  )
}

/** Match Losi Computer / sandbox / canvas / desktop mutate names. */
export function isCrewComputerFamilyToolName(toolName: string): boolean {
  const n = toolName.toLowerCase()
  return (
    n.startsWith('sandbox__') ||
    n.startsWith('canvasbrowser__') ||
    n.startsWith('desktopcomputercontroltool__') ||
    n.startsWith('desktopfilesystemtool__') ||
    n.includes('computer_use') ||
    n.includes('computeruse')
  )
}

/**
 * Honest gate — Never strips are live for send/buy/delete/computer.
 */
export function isCrewCustomRulesEnforcementWired(): boolean {
  return true
}

/**
 * Modes shown in Custom Rules UI.
 * Until enforcement is wired, only Ask is honest — Allow/Never look live.
 */
export function crewCustomRuleModesForUi(): readonly CrewCustomRuleMode[] {
  return isCrewCustomRulesEnforcementWired()
    ? CREW_CUSTOM_RULE_MODES
    : ([CREW_CUSTOM_RULE_MODE_ASK] as const)
}

/**
 * Displayed mode for a tool row. Coerces Allow/Never → Ask while NOT_WIRED.
 */
export function crewCustomRuleDisplayMode(
  mode: CrewCustomRuleMode
): CrewCustomRuleMode {
  return isCrewCustomRulesEnforcementWired()
    ? mode
    : CREW_CUSTOM_RULE_MODE_ASK
}

/**
 * Tool-family rows that ARE enforced server-side today.
 * Never → strip via filterToolsForCustomRules; computer also member-access.
 */
export const CREW_CUSTOM_RULES_ENFORCED_TOOL_KEYS = [
  'computer',
  'send',
  'buy',
  'delete'
] as const

export function isCrewCustomRuleToolEnforced(
  toolKey: CrewCustomRuleToolKey
): boolean {
  return (CREW_CUSTOM_RULES_ENFORCED_TOOL_KEYS as readonly string[]).includes(
    toolKey
  )
}

/** Modes shown for one row — full Ask|Allow|Never only when enforced. */
export function crewCustomRuleModesForTool(
  toolKey: CrewCustomRuleToolKey
): readonly CrewCustomRuleMode[] {
  return isCrewCustomRuleToolEnforced(toolKey)
    ? CREW_CUSTOM_RULE_MODES
    : crewCustomRuleModesForUi()
}

/** Displayed mode for one row — honest coercion for non-enforced rows. */
export function crewCustomRuleDisplayModeForTool(
  toolKey: CrewCustomRuleToolKey,
  mode: CrewCustomRuleMode
): CrewCustomRuleMode {
  return isCrewCustomRuleToolEnforced(toolKey)
    ? mode
    : crewCustomRuleDisplayMode(mode)
}

/**
 * Runtime enforcement status for UI chips / API honesty.
 * Matches prefs stamp + isCrewCustomRulesEnforcementWired().
 */
export function resolveCrewCustomRulesEnforcementStatus(): CrewCustomRulesEnforcementStatus {
  return crewCustomRulesEnforcementStamp()
}
